import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { BarChart3, Trophy, TrendingUp } from 'lucide-react'
import api from '@/services/api'

interface BdrMetric {
  bdrId: string
  bdrName: string
  totalLeads: number
  convertedLeads: number
  conversionRate: number
  totalAttempts: number
  meetingsMonth: number
}

export function BdrMetricsComparison() {
  const [metrics, setMetrics] = useState<BdrMetric[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get('/bdr/metrics-comparison')
        setMetrics(data.data ?? [])
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const sorted = useMemo(
    () => [...metrics].sort((a, b) => b.conversionRate - a.conversionRate),
    [metrics],
  )

  const maxLeads = useMemo(
    () => Math.max(...metrics.map((m) => m.totalLeads), 1),
    [metrics],
  )

  if (loading) {
    return (
      <div className="glass-card border-white/[0.06] p-5 animate-pulse">
        <div className="h-4 bg-white/[0.06] rounded w-48 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-white/[0.04] rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (metrics.length === 0) {
    return null
  }

  const isSingleBdr = sorted.length === 1

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card border-white/[0.06] p-5"
    >
      <div className="flex items-center gap-2 text-text-primary font-extrabold text-xs uppercase tracking-widest mb-5">
        <BarChart3 size={15} className="text-accent shrink-0" />
        {isSingleBdr ? 'Métricas BDR' : 'Comparativo BDRs'}
      </div>

      {/* Header */}
      <div className="grid grid-cols-5 gap-2 mb-3 px-3">
        <span className="text-[9px] font-black text-text-muted uppercase tracking-widest">BDR</span>
        <span className="text-[9px] font-black text-text-muted uppercase tracking-widest text-center">Leads</span>
        <span className="text-[9px] font-black text-text-muted uppercase tracking-widest text-center">Convertidos</span>
        <span className="text-[9px] font-black text-text-muted uppercase tracking-widest text-center">Tentativas</span>
        <span className="text-[9px] font-black text-text-muted uppercase tracking-widest text-center">Taxa</span>
      </div>

      {/* Rows */}
      <div className="space-y-2">
        {sorted.map((m, idx) => {
          const barPct = Math.max((m.totalLeads / maxLeads) * 100, 4)
          const isTop = !isSingleBdr && idx === 0 && m.conversionRate > 0

          return (
            <motion.div
              key={m.bdrId}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="relative flex items-center grid grid-cols-5 gap-2 px-3 py-2.5 bg-white/[0.02] border border-white/[0.04] rounded-xl hover:bg-white/[0.04] hover:border-accent/10 transition-all"
            >
              {/* Bg bar */}
              <div
                className="absolute inset-0 rounded-xl opacity-[0.06]"
                style={{
                  background: `linear-gradient(90deg, ${isTop ? '#3B82F6' : '#6B7280'} 0%, transparent ${barPct}%)`,
                }}
              />

              <div className="relative flex items-center gap-2">
                {isTop && <Trophy size={12} className="text-amber-400 shrink-0" />}
                <span className="text-xs font-bold text-text-primary truncate">{m.bdrName}</span>
              </div>
              <span className="relative text-xs font-bold text-text-primary text-center">{m.totalLeads}</span>
              <span className="relative text-xs font-bold text-center text-emerald-400">{m.convertedLeads}</span>
              <span className="relative text-xs font-bold text-text-muted text-center">{m.totalAttempts}</span>
              <div className="relative flex items-center justify-center gap-1">
                <TrendingUp size={11} className={m.conversionRate >= 25 ? 'text-emerald-400' : 'text-amber-400'} />
                <span className={`text-xs font-extrabold ${m.conversionRate >= 25 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {m.conversionRate}%
                </span>
              </div>
            </motion.div>
          )
        })}
      </div>
    </motion.div>
  )
}
